// src/components/ColorSchemeSelector.tsx
import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext.tsx";
import "./ColorSchemeSelector.css";

interface ColorScheme {
  id: number;
  user_id: string;
  name: string;
  light_color: string;
  dark_color: string;
  created_at: string;
}

interface ColorSchemeSelectorProps {
  lightColor: string;
  darkColor: string;
  onColorChange: (lightColor: string, darkColor: string) => void;
}

const DEFAULT_SCHEMES = [
  { name: "Default Colors", light_color: "#f0d9b5", dark_color: "#b58863" },
  { name: "Blue Theme", light_color: "#d4e4f7", dark_color: "#5b9bd5" },
  { name: "Green Theme", light_color: "#d4f7d4", dark_color: "#5b9d5b" },
  { name: "Purple Theme", light_color: "#e8d4f7", dark_color: "#9d5b9d" },
];

const ColorSchemeSelector: React.FC<ColorSchemeSelectorProps> = ({
  lightColor,
  darkColor,
  onColorChange,
}) => {
  const { user } = useAuth();
  const [savedSchemes, setSavedSchemes] = useState<ColorScheme[]>([]);
  const [selectedSchemeId, setSelectedSchemeId] = useState<number | null>(null);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [schemeName, setSchemeName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<number | null>(null);
  const [showLoginAlert, setShowLoginAlert] = useState(false);
  const API_URL = process.env.REACT_APP_API_URL || "";

  // Fetch saved schemes on mount
  useEffect(() => {
    if (user) {
      fetchColorSchemes();
    }
  }, [user]);

  const fetchColorSchemes = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;

      const response = await fetch(`${API_URL}/api/color-schemes`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setSavedSchemes(data.schemes);
      }
    } catch (error) {
      console.error("Error fetching color schemes:", error);
    }
  };

  const MAX_COLOR_SCHEMES = 12;

  const handleSaveScheme = async () => {
    if (!schemeName.trim()) {
      setError("Please enter a name for the color scheme");
      return;
    }

    if (schemeName.length > 50) {
      setError("Name must be 50 characters or less");
      return;
    }

    if (savedSchemes.length >= MAX_COLOR_SCHEMES) {
      setError(`Maximum of ${MAX_COLOR_SCHEMES} color schemes allowed. Please delete one first.`);
      return;
    }

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        setError("You must be logged in to save color schemes");
        return;
      }

      const response = await fetch(`${API_URL}/api/color-schemes`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: schemeName,
          light_color: lightColor,
          dark_color: darkColor,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setSuccessMessage("Color scheme saved successfully!");
        setSchemeName("");
        setShowSaveDialog(false);
        fetchColorSchemes();
        setTimeout(() => setSuccessMessage(null), 5000);
      } else {
        setError(data.error || "Failed to save color scheme");
      }
    } catch (error) {
      console.error("Error saving color scheme:", error);
      setError("Failed to save color scheme");
    }
  };

  const handleDeleteScheme = (
    schemeId: number,
    event: React.MouseEvent
  ) => {
    event.stopPropagation();
    setShowDeleteConfirm(schemeId);
  };

  const confirmDeleteScheme = async () => {
    if (!showDeleteConfirm) return;
    const schemeId = showDeleteConfirm;
    setShowDeleteConfirm(null);

    try {
      const token = localStorage.getItem("token");
      if (!token) return;

      const response = await fetch(`${API_URL}/api/color-schemes/${schemeId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        setSuccessMessage("Color scheme deleted successfully!");
        if (selectedSchemeId === schemeId) {
          setSelectedSchemeId(null);
        }
        fetchColorSchemes();
        setTimeout(() => setSuccessMessage(null), 5000);
      } else {
        const data = await response.json();
        setError(data.error || "Failed to delete color scheme");
      }
    } catch (error) {
      console.error("Error deleting color scheme:", error);
      setError("Failed to delete color scheme");
    }
  };

  const handleSelectScheme = (
    schemeId: number | null,
    lightColor: string,
    darkColor: string
  ) => {
    setSelectedSchemeId(schemeId);
    onColorChange(lightColor, darkColor);
  };

  return (
    <div className="color-scheme-selector-wrapper">
      {/* Save Button - placed next to dark color display */}
      <button
        onClick={() => {
          if (!user) {
            setShowLoginAlert(true);
            return;
          }
          setShowSaveDialog(true);
          setError(null);
        }}
        className="save-color-scheme-btn"
        title="Save current colors"
      >
        💾 Save
      </button>

      {/* Dropdown Selector */}
      {user && (
        <div className="color-scheme-dropdown-container">
          <select
            value={selectedSchemeId || ""}
            onChange={(e) => {
              const value = e.target.value;
              if (value === "") {
                setSelectedSchemeId(null);
                return;
              }

              const schemeId = parseInt(value);
              const scheme = savedSchemes.find((s) => s.id === schemeId);
              if (scheme) {
                handleSelectScheme(
                  schemeId,
                  scheme.light_color,
                  scheme.dark_color
                );
              } else {
                // Handle default schemes
                const defaultScheme = DEFAULT_SCHEMES[schemeId - 1000];
                if (defaultScheme) {
                  handleSelectScheme(
                    null,
                    defaultScheme.light_color,
                    defaultScheme.dark_color
                  );
                }
              }
            }}
            className="color-scheme-select"
          >
            <option value="">Select Color Scheme</option>
            <optgroup label="Default Schemes">
              {DEFAULT_SCHEMES.map((scheme, index) => (
                <option key={`default-${index}`} value={1000 + index}>
                  {scheme.name}
                </option>
              ))}
            </optgroup>
            {savedSchemes.length > 0 && (
              <optgroup label="My Saved Schemes">
                {savedSchemes.map((scheme) => (
                  <option key={scheme.id} value={scheme.id}>
                    {scheme.name}
                  </option>
                ))}
              </optgroup>
            )}
          </select>

          {/* Color preview icons */}
          <div className="color-preview-container">
            {savedSchemes.map((scheme) => (
              <div
                key={scheme.id}
                className={`color-preview-item ${
                  selectedSchemeId === scheme.id ? "selected" : ""
                }`}
                onClick={() =>
                  handleSelectScheme(
                    scheme.id,
                    scheme.light_color,
                    scheme.dark_color
                  )
                }
                title={scheme.name}
              >
                <div className="color-preview-boxes">
                  <div
                    className="color-preview-box"
                    style={{ backgroundColor: scheme.light_color }}
                  />
                  <div
                    className="color-preview-box"
                    style={{ backgroundColor: scheme.dark_color }}
                  />
                </div>
                <button
                  className="delete-scheme-btn"
                  onClick={(e) => handleDeleteScheme(scheme.id, e)}
                  title="Delete scheme"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Save Dialog Modal */}
      {showSaveDialog && (
        <div
          className="save-dialog-overlay"
          onClick={() => setShowSaveDialog(false)}
        >
          <div className="save-dialog" onClick={(e) => e.stopPropagation()}>
            <h3>Save Color Scheme</h3>
            <div className="color-preview-save">
              <div
                className="color-box"
                style={{ backgroundColor: lightColor }}
              >
                <span>Light</span>
              </div>
              <div className="color-box" style={{ backgroundColor: darkColor }}>
                <span>Dark</span>
              </div>
            </div>
            <input
              type="text"
              placeholder="Enter scheme name..."
              value={schemeName}
              onChange={(e) => {
                setSchemeName(e.target.value);
                setError(null);
              }}
              maxLength={50}
              className="scheme-name-input"
              autoFocus
            />
            {error && <div className="error-message">{error}</div>}
            <div className="save-dialog-actions">
              <button
                onClick={() => {
                  setShowSaveDialog(false);
                  setError(null);
                  setSchemeName("");
                }}
                className="btn-cancel"
              >
                Cancel
              </button>
              <button onClick={handleSaveScheme} className="btn-save">
                Save Scheme
              </button>
            </div>
            <div className="scheme-count-info">
              {savedSchemes.length} / 12 schemes saved
            </div>
          </div>
        </div>
      )}

      {/* Success/Error Messages */}
      {successMessage && <div className="success-toast">{successMessage}</div>}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="save-dialog-overlay" onClick={() => setShowDeleteConfirm(null)}>
          <div className="save-dialog" onClick={(e) => e.stopPropagation()}>
            <h3>🗑️ Delete Color Scheme</h3>
            <p style={{ margin: "15px 0", color: "#333" }}>
              Are you sure you want to delete this color scheme?
            </p>
            <p style={{ margin: "10px 0 20px", color: "#666", fontSize: "14px" }}>
              This action cannot be undone.
            </p>
            <div className="save-dialog-actions">
              <button
                onClick={() => setShowDeleteConfirm(null)}
                className="btn-cancel"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteScheme}
                className="btn-save"
                style={{ background: "#f44336" }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Login Required Modal */}
      {showLoginAlert && (
        <div className="save-dialog-overlay" onClick={() => setShowLoginAlert(false)}>
          <div className="save-dialog" onClick={(e) => e.stopPropagation()}>
            <h3>🔐 Login Required</h3>
            <p style={{ margin: "15px 0 20px", color: "#333" }}>
              Please log in to save color schemes.
            </p>
            <div className="save-dialog-actions">
              <button
                onClick={() => setShowLoginAlert(false)}
                className="btn-save"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ColorSchemeSelector;
